import type { ReactElement, ReactNode } from 'react';
import { isValidElement } from 'react';
import styled, { css } from 'styled-components';

import LoggingService from 'components/core/logging/LoggingService';
import PrimaryText from 'components/core/typography/PrimaryText';
import Text from 'components/core/typography/Text';
import { DeletedUserIcon, UserIcon, VehicleIcon } from 'components/ui/icons';
import { ImageType } from 'enums/imageType';
import { formatInitials } from 'utils/formatUtils';

import type { Contact, Lead, User } from 'store/client/graphql';

import { BODY_TEXT_TERTIARY } from 'styles/color';
import {
  BLUE_500,
  BORDER_RADIUS_200,
  FONT_SIZE_44,
  FONT_WEIGHT_BOLDEST,
  GREEN_500,
  NEUTRAL_0,
  NEUTRAL_050,
  NEUTRAL_075,
  NEUTRAL_200,
  YELLOW_500,
} from 'styles/tokens';

export const FallbackIcon = styled.div`
  align-items: center;
  background: ${NEUTRAL_050};
  border-radius: ${BORDER_RADIUS_200};
  color: ${BODY_TEXT_TERTIARY};
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;

  > ${Text} {
    &${PrimaryText} {
      color: ${NEUTRAL_0};
    }
  }
`;

export const FallbackImage = styled.div`
  align-items: center;
  background: ${NEUTRAL_075};
  color: ${NEUTRAL_200};
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;

  svg {
    width: 50%;
  }
`;

const HeroText = styled(PrimaryText)`
  font-size: ${FONT_SIZE_44};
  font-weight: ${FONT_WEIGHT_BOLDEST};
`;

export const getUserColor = (user: { id?: string; rgbHex?: string }): string => {
  if (user?.rgbHex) {
    return user.rgbHex;
  }

  switch (Number.parseInt(user.id || '0', 16) % 4) {
    case 1: {
      return GREEN_500;
    }

    case 2: {
      return YELLOW_500;
    }

    case 3: {
      return BLUE_500;
    }

    default: {
      return YELLOW_500;
    }
  }
};

const renderAvatarFallback = (): ReactNode => (
  <FallbackIcon
    css={css`
      background: ${BODY_TEXT_TERTIARY};
    `}
  >
    <UserIcon height={20} width={20} />
  </FallbackIcon>
);

const renderHeroFallback = (fallbackSource: ReactElement<any> | undefined | User): ReactNode => {
  if (isValidElement(fallbackSource)) {
    return <FallbackImage>{fallbackSource}</FallbackImage>;
  }

  if (fallbackSource) {
    return (
      <FallbackIcon style={{ background: getUserColor(fallbackSource as User) }}>
        <HeroText>{formatInitials(fallbackSource as User)}</HeroText>
      </FallbackIcon>
    );
  }

  return null;
};

const renderIconFallback = (fallbackSource: ReactElement<any> | undefined): ReactNode => (
  <FallbackIcon>{fallbackSource}</FallbackIcon>
);

const renderInventoryItemFallback = (): ReactNode => (
  <FallbackImage>
    <VehicleIcon />
  </FallbackImage>
);

const renderPhotoFallback = (fallbackSource: ReactElement<any> | undefined): ReactNode =>
  fallbackSource ? <FallbackImage>{fallbackSource}</FallbackImage> : null;

type UserFallbackInfo =
  | (Optional<Pick<Contact | Lead, 'firstName' | 'lastName' | 'rgbHex'>, 'lastName'> & { deleted?: undefined })
  | Optional<Pick<User, 'deleted' | 'firstName' | 'lastName' | 'rgbHex'>, 'deleted' | 'lastName'>;

const renderUserFallback = (fallbackSource: UserFallbackInfo): ReactNode => {
  const isDeleted = fallbackSource.deleted;

  return (
    <FallbackIcon style={{ background: isDeleted ? BODY_TEXT_TERTIARY : getUserColor(fallbackSource) }}>
      {isDeleted ? <DeletedUserIcon color={NEUTRAL_0} /> : <PrimaryText>{formatInitials(fallbackSource)}</PrimaryText>}
    </FallbackIcon>
  );
};

export const logUnhandledFallbackType = (type: ImageType | undefined): void => {
  LoggingService.debug({ message: `Unhandled fallback Image: ${type}` });
};

interface FallbackImageProps {
  fallbackSrc?: ReactElement<any> | UserFallbackInfo;
  type?: ImageType;
}

export const renderFallbackForType = ({ fallbackSrc, type }: FallbackImageProps): ReactNode => {
  switch (type) {
    case ImageType.AVATAR: {
      return renderAvatarFallback();
    }

    case ImageType.HERO: {
      return renderHeroFallback(fallbackSrc as ReactElement<any> | undefined | User);
    }

    case ImageType.ICON: {
      return renderIconFallback(fallbackSrc as ReactElement<any> | undefined);
    }

    case ImageType.INVENTORY_ITEM: {
      return renderInventoryItemFallback();
    }

    case ImageType.PHOTO: {
      return renderPhotoFallback(fallbackSrc as ReactElement<any> | undefined);
    }

    case ImageType.USER: {
      return renderUserFallback(fallbackSrc as UserFallbackInfo);
    }

    default: {
      logUnhandledFallbackType(type);

      return null;
    }
  }
};
